
if (!customElements.get('custom-ctrl-size')) {
  class CustomCtrlSize extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback () { this.updateSizeButton(); }

    updateSizeButton () {
      const hasSizeImage = this.section && this.section.querySelector('.media-has_size_image');
      if (!hasSizeImage) return;

      this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
      this.firstSize = hasSizeImage.querySelector('.custom-media-size-image');
      this.firstImage = hasSizeImage.querySelectorAll('.media-viewer__item')[0];

      // One delegated listener — survives DOM rebuilds, no leak on re-render.
      if (!this._bound) {
        this.section.addEventListener('click', this._onClick);
        this._bound = true;
      }
    }

    _onClick (e) {
      const btn = e.target.closest('.media-ctrl__counter_new');
      if (!btn || btn.classList.contains('active')) return;

      this.section
        .querySelectorAll('.media-ctrl__counter_new.active')
        .forEach(el => el.classList.remove('active'));
      btn.classList.add('active');

      if (!this.mediaGallery) return;
      // index based on sibling position — no need to bake data-index into markup.
      const index = Array.prototype.indexOf.call(btn.parentNode.children, btn);
      const target = index === 0 ? this.firstImage : this.firstSize;
      if (target) this.mediaGallery.customSetActiveMedia(target, true);
    }

    disconnectedCallback () {
      if (this._bound) {
        this.section.removeEventListener('click', this._onClick);
        this._bound = false;
      }
    }
  }
  customElements.define('custom-ctrl-size', CustomCtrlSize);
}

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.findForms(true)[0] || null;
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    findForms (findProduct = false) {
      if (this.dataset.inQuickAddDrawer === 'true') {
        const drawer = this.closest('quick-add-drawer');
        if (drawer) {
          if (findProduct) return Array.from(drawer.querySelectorAll('.js-product-form-main'));
          return Array.from(drawer.querySelectorAll('.js-product-form-main, .js-instalments-form'));
        }
      }
      if (findProduct) return Array.from(this.section.querySelectorAll('.js-product-form-main'));
      return Array.from(this.section.querySelectorAll('.js-product-form-main, .js-instalments-form'));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange (evt) {
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();

      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateDetailPopup();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton () {
      this.productForm = this.findForms(true)[0] || null;
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability (optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability () {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();

      if (!this.variant) {
        currVariant = { options: selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          // 第一个选项组不需要标记为不可用
          if (optionEl.closest('fieldset')?.dataset.index == "1") return
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText () {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText (evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia () {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;
        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability () {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Recalculates the .product-detail-popup prices/amount after a variant change.
     * Re-computes the discount using the exact rule bound on the popup
     * (data-pdp-discount-type / data-pdp-discount-value) instead of a proportional estimate.
     *   - percentage:  off = floor(price * value / 100)
     *   - fixed_amount: off = value * 100 (value is in MAJOR units, e.g. 5 → $5.00)
     * Formatting uses the shop's money format so the displayed strings
     * match the cart-drawer (which also uses `| money`).
     */
    updateDetailPopup () {
      const popup = this.section.querySelector('.product-detail-popup');
      const pdpDrawer = document.querySelector('.pdp-coupon-drawer');
      if (!popup || !pdpDrawer || !this.variant) return;

      const type = (popup.dataset.pdpDiscountType || 'fixed_amount').toLowerCase();
      const value = parseFloat(popup.dataset.pdpDiscountValue) || 0;
      if (!value) return;

      // 精确分支计算折扣（cents，整数）
      let offCents;
      if (type === 'percentage') {
        offCents = Math.floor((this.variant.price * value) / 100);
      } else {
        // fixed_amount: value 为主单位（$5 = 5），cents = value * 100
        offCents = Math.round(value * 100);
      }
      // 折扣不能超过商品价
      offCents = Math.min(offCents, this.variant.price);

      const finalCents = this.variant.price - offCents;

      // 与 `| money` 对齐：cents → major unit（两位小数）→ 套用 shop.money_format
      const moneyFormat =
        (window.Shopify && Shopify.money_format) || '${{amount}}';
      const fmt = (cents) => {
        const major = (cents / 100).toFixed(2);
        const withCommas = major.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return moneyFormat
          .replace(/{{\s*amount_with_comma_separator\s*}}/g, withCommas)
          .replace(/{{\s*amount\s*}}/g, major);
      };

      const newOffStr = fmt(offCents);
      const newFinalStr = fmt(finalCents);
      // Regular price 直接复用 server 端已格式化的字符串，符号一致
      const newPriceStr = this.data.formatted[this.variant.id].price;

      const triggerSpan = popup.querySelector('[data-pdp-coupon-trigger] span');
      if (triggerSpan) triggerSpan.textContent = `Claim ${newOffStr} Off`;

      const setText = (selector, text) => {
        const el = pdpDrawer.querySelector(selector);
        if (el) el.textContent = text;
      };
      setText('[data-pdp-coupon-after-price]', newFinalStr);
      setText('[data-pdp-coupon-regular-price]', newPriceStr);
      setText('[data-pdp-coupon-final-price]', newFinalStr);
      setText('[data-pdp-coupon-amount-off]', `-${newOffStr}`);
    }

    /**
     * Updates the price.
     */
    updatePrice () {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');

      if (!this.price) return;

      if (this.variant) {




        const newPrice = (this.variant.price / 100).toFixed(2);
        // 找到 PayPal 消息的 div
        const paypalMessageDiv = document.querySelector('[data-pp-message]');
        if (paypalMessageDiv) {
          // 更新 'data-pp-amount' 属性
          paypalMessageDiv.setAttribute('data-pp-amount', newPrice);
          setTimeout(() => {
            if (window.paypal && window.paypal.Messages) {
              window.paypal.Messages.render(paypalMessageDiv);
            }
          }, 50);
        }




        // console.log('variant',this.variant);

        // console.log('this.variant.compare_at_price',this.variant.compare_at_price);
        // console.log('this.variant.price',this.variant.price);

        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');
        const custom_compare_percent = this.price.querySelector('.custom_compare_percent');
        // console.log('custom_compare_percent',custom_compare_percent);


        if (this.variant.compare_at_price > this.variant.price) {
          custom_compare_percent.classList.remove("d-none")
          let discount = 100 - Math.round((this.variant.price * 100) / this.variant.compare_at_price)
          custom_compare_percent.querySelector("span").innerHTML = discount

          custom_compare_percent.style.display = 'inline-block;'

        } else {
          custom_compare_percent.classList.add("d-none")
          custom_compare_percent.querySelector("span").innerHTML = ''

          custom_compare_percent.style.display = 'none'



        }
        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant;
      this.price.querySelector('.price__no-variant').hidden = this.variant;
    }

    /**
     * Updates the weight.
     */
    updateWeight () {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode () {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku () {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl (evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput () {
      this.forms = this.findForms();

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions () {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData () {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant () {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
